<script setup lang="ts">
import type { TemplateCardView, TemplateShelf, TemplateShelfFilter } from '~/utils/template-catalog'

/**
 * The marketplace, browsed: a curated start, the shelves, and everything.
 *
 * It opened on all 105 apps at once, two columns in a box that scrolled inside
 * the page. Now it opens on the few apps most people come for, the shelves sit
 * above as chips that WRAP (the old row scrolled sideways, so half the
 * categories were never seen on a desktop), and "All" lists every app under its
 * shelf. Nothing here scrolls inside the page.
 */
const props = defineProps<{
  views: TemplateCardView[]
  shelf: TemplateShelfFilter
  modelValue: string | null
}>()

const emit = defineEmits<{
  'update:shelf': [value: TemplateShelfFilter]
  select: [code: string, listId: string]
}>()

const { t } = useI18n()

const byName = (a: TemplateCardView, b: TemplateCardView) => a.template.name.localeCompare(b.template.name)

const featuredViews = computed(() =>
  FEATURED_APP_CODES.map((code) => props.views.find((view) => view.code === code)).filter(
    (view): view is TemplateCardView => !!view
  )
)
const newViews = computed(() => props.views.filter((view) => view.isNew).sort(byName))

const shelves = computed(() => {
  const groups = new Map<TemplateShelf, TemplateCardView[]>()
  for (const view of props.views) {
    const shelf = view.shelf ?? 'other'
    groups.set(shelf, [...(groups.get(shelf) ?? []), view])
  }
  return TEMPLATE_SHELF_ORDER.filter((shelf) => groups.has(shelf)).map((shelf) => ({
    shelf,
    label: t(`billing.page.checkout.os.marketplaceCategories.${shelf}`),
    description: t(`billing.page.checkout.os.catalog.shelves.${shelf}`),
    icon: TEMPLATE_SHELF_STYLE[shelf].icon,
    views: groups.get(shelf)!.sort(byName),
  }))
})

const chips = computed(() => [
  ...(featuredViews.value.length
    ? [{ key: 'featured' as const, label: t('billing.page.checkout.os.catalog.chips.featured'), icon: 'i-lucide-sparkles', count: null }]
    : []),
  ...(newViews.value.length
    ? [{ key: 'new' as const, label: t('billing.page.checkout.os.catalog.chips.new'), icon: 'i-lucide-badge-plus', count: newViews.value.length }]
    : []),
  { key: 'all' as const, label: t('billing.page.checkout.os.catalog.chips.all'), icon: 'i-lucide-layout-grid', count: props.views.length },
  ...shelves.value.map((group) => ({ key: group.shelf, label: group.label, icon: group.icon, count: group.views.length })),
])

/** A filter that holds nothing (a shelf emptied by a catalog change) falls back to all. */
const activeShelf = computed<TemplateShelfFilter>(() =>
  chips.value.some((chip) => chip.key === props.shelf) ? props.shelf : 'all'
)

const visibleViews = computed<TemplateCardView[]>(() => {
  if (activeShelf.value === 'featured') return featuredViews.value
  if (activeShelf.value === 'new') return newViews.value
  if (activeShelf.value === 'all') return shelves.value.flatMap((group) => group.views)
  return shelves.value.find((group) => group.shelf === activeShelf.value)?.views ?? []
})

const description = computed(() =>
  t(`billing.page.checkout.os.catalog.shelves.${activeShelf.value}`)
)

// ── Keyboard ───────────────────────────────────────────────────────────────
const chipGroup = ref<HTMLElement | null>(null)

/** Chips are a filter: moving between them is free, so selection follows focus. */
function onChipKeydown(event: KeyboardEvent) {
  const keys = chips.value.map((chip) => chip.key)
  const index = keys.indexOf(activeShelf.value)
  const next =
    event.key === 'ArrowRight' || event.key === 'ArrowDown'
      ? keys[(index + 1) % keys.length]
      : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
        ? keys[(index - 1 + keys.length) % keys.length]
        : event.key === 'Home'
          ? keys[0]
          : event.key === 'End'
            ? keys[keys.length - 1]
            : null
  if (!next) return
  event.preventDefault()
  emit('update:shelf', next)
  // By attribute, not a template-ref array: Vue does not promise that array's order.
  nextTick(() => chipGroup.value?.querySelector<HTMLElement>(`[data-shelf="${next}"]`)?.focus())
}

const grid = ref<HTMLElement | null>(null)
const roving = useRovingGrid(grid, {
  ids: () => visibleViews.value.map((view) => view.code),
  preferred: () => props.modelValue,
})

function listId(): string {
  return activeShelf.value === 'featured' || activeShelf.value === 'new' || activeShelf.value === 'all'
    ? activeShelf.value
    : `shelf:${activeShelf.value}`
}
</script>

<template>
  <div class="space-y-3">
    <div class="relative">
      <div
        ref="chipGroup"
        role="radiogroup"
        :aria-label="$t('billing.page.checkout.os.filterLabel')"
        class="-mx-1 flex gap-2 overflow-x-auto px-1 py-1 [scrollbar-width:none] @lg:flex-wrap @lg:overflow-visible [&::-webkit-scrollbar]:hidden"
        @keydown="onChipKeydown"
      >
        <button
          v-for="chip in chips"
          :key="chip.key"
          type="button"
          role="radio"
          data-testid="marketplace-shelf"
          :data-shelf="chip.key"
          :aria-checked="activeShelf === chip.key"
          :tabindex="activeShelf === chip.key ? 0 : -1"
          :class="[
            'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-default',
            activeShelf === chip.key
              ? 'border-primary bg-primary text-inverted'
              : 'border-default bg-default text-toned hover:border-accented hover:text-highlighted',
          ]"
          @click="emit('update:shelf', chip.key)"
        >
          <UIcon :name="chip.icon" class="size-3.5" />
          {{ chip.label }}
          <span
            v-if="chip.count !== null"
            :class="['tabular-nums', activeShelf === chip.key ? 'text-inverted' : 'text-muted']"
          >{{ chip.count }}</span>
        </button>
      </div>
      <!-- On a phone the chips scroll sideways; the fade says there is more. -->
      <div
        class="pointer-events-none absolute inset-y-0 right-0 w-10 bg-linear-to-l from-(--ui-bg) to-transparent @lg:hidden"
        aria-hidden="true"
      />
    </div>

    <p class="text-xs text-muted">{{ description }}</p>

    <!-- One radiogroup per shelf under "All" (a heading is not a radio, and a
         radiogroup may own only radios); one for the whole list otherwise. The
         roving tab stop spans all of them: Tab enters once, arrows cross shelves. -->
    <div
      ref="grid"
      :role="activeShelf === 'all' ? undefined : 'radiogroup'"
      :aria-label="activeShelf === 'all' ? undefined : $t('billing.page.checkout.os.tabMarketplace')"
      class="space-y-5"
      @keydown="roving.onKeydown"
      @focusin="roving.onFocusin"
    >
      <template v-if="activeShelf === 'all'">
        <section v-for="group in shelves" :key="group.shelf" class="space-y-2">
          <h3
            :id="`template-shelf-${group.shelf}`"
            class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted"
          >
            <UIcon :name="group.icon" class="size-3.5" />
            {{ group.label }}
            <span class="font-normal tabular-nums text-muted">{{ group.views.length }}</span>
          </h3>
          <div
            role="radiogroup"
            :aria-labelledby="`template-shelf-${group.shelf}`"
            class="grid grid-cols-1 gap-2.5 @md:grid-cols-2 @4xl:grid-cols-3"
          >
            <CheckoutTemplatePickerAppCard
              v-for="view in group.views"
              :key="view.code"
              :view="view"
              :selected="modelValue === view.code"
              :show-shelf="false"
              :tabindex="roving.tabStop.value === view.code ? 0 : -1"
              @select="emit('select', view.code, listId())"
            />
          </div>
        </section>
      </template>

      <div v-else class="grid grid-cols-1 gap-2.5 @md:grid-cols-2 @4xl:grid-cols-3">
        <CheckoutTemplatePickerAppCard
          v-for="view in visibleViews"
          :key="view.code"
          :view="view"
          :selected="modelValue === view.code"
          :show-shelf="activeShelf === 'featured' || activeShelf === 'new'"
          :tabindex="roving.tabStop.value === view.code ? 0 : -1"
          @select="emit('select', view.code, listId())"
        />
      </div>
    </div>

    <div v-if="activeShelf === 'featured'" class="flex justify-center pt-1">
      <UButton
        color="neutral"
        variant="outline"
        trailing-icon="i-lucide-arrow-right"
        @click="emit('update:shelf', 'all')"
      >
        {{ $t('billing.page.checkout.os.catalog.viewAll', { count: views.length }) }}
      </UButton>
    </div>
  </div>
</template>
